// src/tattoo/pipeline.js
import * as THREE from "three";

// ---- perf caches (module-level) ----
const _prepCache = new Map(); // key -> Canvas
const _imgCacheKey = new Map(); // key -> string(url+mask+edgeClip+blend) for invalidation

/** -------- public helpers you already use -------- */
export function canvasToTexture(canvas) {
  const tex = new THREE.CanvasTexture(canvas);
  tex.flipY = false;
  if ("colorSpace" in tex) tex.colorSpace = THREE.SRGBColorSpace;

  // iPad/WebGL safety for NPOT canvas textures
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 1;

  tex.needsUpdate = true;
  return tex;
}

// pipeline.js
export function buildLayersSignature(partKey, layers, edgeClip) {
  return JSON.stringify({
    partKey,
    edgeClip,
    layers: (layers || []).map((l) => {
      const t = l.transform || {};
      // only include fields that actually affect the baked texture
      const cleanT = {
        tx: t.tx ?? 0.5,
        ty: t.ty ?? 0.5,
        scale: t.scale,
        scaleX: t.scaleX,
        scaleY: t.scaleY,
        rot: t.rot,
      };

      return {
        id: l.id,
        vis: !!l.visible,
        op: +l.opacity,
        bl: l.blend,
        ek: l.edgeClip,
        pk: l.partKeys,
        t: cleanT,
      };
    }),
  });
}

/* ---------------- internal helpers ---------------- */
function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function normalizeT(t = {}) {
  // base defaults
  const out = {
    tx: 0.5,
    ty: 0.5,
    scale: 1,
    scaleX: 1,
    scaleY: 1,
    rot: 0,
  };

  if (typeof t.tx === "number") out.tx = t.tx;
  if (typeof t.ty === "number") out.ty = t.ty;

  // base uniform scale
  let baseScale = 1;
  if (typeof t.scale === "number" && isFinite(t.scale)) {
    baseScale = t.scale;
    out.scale = t.scale;
  }

  // free-transform axes with fallback to baseScale
  if (typeof t.scaleX === "number" && isFinite(t.scaleX)) {
    out.scaleX = t.scaleX;
  } else {
    out.scaleX = baseScale;
  }

  if (typeof t.scaleY === "number" && isFinite(t.scaleY)) {
    out.scaleY = t.scaleY;
  } else {
    out.scaleY = baseScale;
  }

  if (typeof t.rot === "number") out.rot = t.rot;

  // legacy support
  if (t.offset && typeof t.offset.x === "number") out.tx = t.offset.x;
  if (t.offset && typeof t.offset.y === "number") out.ty = t.offset.y;
  if (typeof t.rotate === "number") out.rot = t.rotate;

  // 🔹 new: skew, clamped so tan() doesn't go crazy
  const clampSkew = (v) => {
    if (!Number.isFinite(v)) return 0;
    const max = 0.8; // ~46 degrees in either direction – plenty
    return Math.max(-max, Math.min(max, v));
  };

  out.skewX = clampSkew(t.skewX);
  out.skewY = clampSkew(t.skewY);

  // horizontal mirror (Merch Ally back-of-blade). Honoured by
  // _applyLayerTransform (flattened UV / print exports).
  out.flipX = !!t.flipX;

  return out;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Cache URLs that failed once so we never retry them (prevents repeated blob: GET errors)
const DEAD_URLS = new Set();

async function safeLoad(url) {
  if (!url) return null;
  if (DEAD_URLS.has(url)) return null;
  try {
    const img = await loadImage(url);
    return img;
  } catch (e) {
    DEAD_URLS.add(url);
    console.warn("[pipeline] Skipping dead image URL:", url, e);
    return null;
  }
}

/** Draw guide without stretching (cover fit into the square) */
function drawGuideCover(ctx, img, size, alpha = 0.32) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.max(size / iw, size / ih); // cover
  const dw = iw * s,
    dh = ih * s;
  const dx = (size - dw) * 0.5;
  const dy = (size - dh) * 0.5;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "source-over";
  ctx.drawImage(img, dx, dy, dw, dh);
  ctx.restore();
}

/** Letterbox the source into a square (object-fit: contain), no distortion */
function drawContain(ctx, img, size) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const s = Math.min(size / iw, size / ih); // contain
  const dw = iw * s,
    dh = ih * s;
  const dx = (size - dw) * 0.5;
  const dy = (size - dh) * 0.5;
  ctx.drawImage(img, dx, dy, dw, dh);
}

/** Prep passes (preserve aspect) */
function normalPass(img, { size, edgeClip }) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, size, size);

  drawContain(ctx, img, size);

  if (edgeClip > 0) {
    const imd = ctx.getImageData(0, 0, size, size);
    const p = imd.data;
    const clip = Math.round(clamp01(edgeClip) * 255);
    for (let i = 0; i < p.length; i += 4) if (p[i + 3] < clip) p[i + 3] = 0;
    ctx.putImageData(imd, 0, 0);
  }
  return c;
}

/** Alpha → solid white (for "white fill" layers). Was referenced by
 *  buildPartComposite/_prepLayerForMerge but never defined. */
function whiteMaskPass(img, { size, edgeClip }) {
  const c = normalPass(img, { size, edgeClip });
  const ctx = c.getContext("2d");
  ctx.save();
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  ctx.restore();
  return c;
}

function multiplyPass(img, { size, edgeClip }) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, size, size);

  drawContain(ctx, img, size);

  const imd = ctx.getImageData(0, 0, size, size);
  const p = imd.data;
  const clip = Math.round(clamp01(edgeClip) * 255);

  for (let i = 0; i < p.length; i += 4) {
    let r = p[i] / 255,
      g = p[i + 1] / 255,
      b = p[i + 2] / 255,
      a = p[i + 3] / 255;
    if (a > 1e-3) {
      // un-premultiply-ish then bake multiply against white
      r = clamp01((r - (1 - a)) / a);
      g = clamp01((g - (1 - a)) / a);
      b = clamp01((b - (1 - a)) / a);
    } else {
      r = g = b = 1;
    }
    r = 1 - a * (1 - r);
    g = 1 - a * (1 - g);
    b = 1 - a * (1 - b);
    if (p[i + 3] < clip) p[i + 3] = 0;
    p[i] = Math.round(r * 255);
    p[i + 1] = Math.round(g * 255);
    p[i + 2] = Math.round(b * 255);
  }
  ctx.putImageData(imd, 0, 0);
  return c;
}

// src/tattoo/pipeline.js

// ===============================
// FAST PREVIEW: static base + 1 moving layer
// ===============================
const __previewCache = {
  key: null,
  size: 0,
  partKey: null,
  baseCanvas: null, // pre-rendered composite of all NON-drag layers
  prepByLayerId: new Map(), // cached prep canvases per layer url+blend+edgeClip
};

function _prepCacheKey(L, size, edgeClip) {
  return [
    L.id,
    L.url,
    (L.blend || "skin").toLowerCase(),
    L.edgeClip ?? edgeClip,
    size,
  ].join("|");
}

async function getPreparedLayerCanvas(img, L, { size, edgeClip }) {
  // Decide prep pass (same logic you already use)
  const rawBlend = (L.blend || "skin").toLowerCase();
  let blend = rawBlend;
  if (blend === "white-ink" || blend === "overlay") blend = "white";

  if (blend === "skin") {
    return multiplyPass(img, { size, edgeClip: L.edgeClip ?? edgeClip });
  }
  return normalPass(img, { size, edgeClip: L.edgeClip ?? edgeClip });
}

/** -------- body-part composite used by PartModel (unchanged transforms) -------- */
export async function buildPartComposite(
  partKey,
  layers,
  { size = 2048, edgeClip = 0.15, guideUrl = null, dragPreview = null } = {}
) {
  // ✅ Read drag state once (single source of truth)
  const drag =
    (typeof window !== "undefined" && window.__tt_decal_drag) || null;

  // ✅ FAST DRAG MODE: drop composite resolution while dragging (mobile win)
  if (
    drag?.active ||
    (typeof window !== "undefined" && window.__tt_decal_drag_active)
  ) {
    size = Math.min(size, 2048); // try 768 first (you can drop to 512 if needed)
  }

  // ✅ DO NOT force size=512 here. Let PartModel decide texSize via isDragging.
  // If you want a cap, do this:
  // if (drag?.active) size = Math.min(size, 1024);

  /// 🟤 Base (dark ink / normal stuff)
const base = document.createElement("canvas");
base.width = size;
base.height = size;
const ctxBase = base.getContext("2d");

// Start clean
ctxBase.globalCompositeOperation = "source-over";
ctxBase.clearRect(0, 0, size, size);

// ✅ important: make empty pixels WHITE so Multiply doesn't darken the skin
ctxBase.fillStyle = "#ffffff";
ctxBase.fillRect(0, 0, base.width, base.height);


// IMPORTANT: base must start WHITE so Multiply is neutral in empty areas.
// Do not clear after this or background becomes transparent black and darkens skin.

  // ⚪ Overlay / white-ink canvas (starts transparent)
  const overlay = document.createElement("canvas");
  overlay.width = size;
  overlay.height = size;
  const ctxOverlay = overlay.getContext("2d");
  ctxOverlay.clearRect(0, 0, size, size);

  // 🟣 Underlay (white-only proxy for ink to multiply over later)
  const underlay = document.createElement("canvas");
  underlay.width = size;
  underlay.height = size;
  const ctxUnder = underlay.getContext("2d");
  ctxUnder.clearRect(0, 0, size, size);

  // optional UV guide → only on base
  if (guideUrl) {
    const g = await safeLoad(guideUrl);
    if (g) drawGuideCover(ctxBase, g, size, 0.32);
  }

  // 🔁 IMPORTANT: draw from *bottom* row to *top* row in the panel
  const ordered = [...(layers || [])].reverse();

  for (const L of ordered) {
    if (!L?.visible || !L?.url) continue;

    // respect part
    if (
      Array.isArray(L.partKeys) &&
      L.partKeys.length &&
      !L.partKeys.includes(partKey)
    ) {
      continue;
    }
    // ---- decide blend + target canvas ----
    const rawBlend = (L.blend || "skin").toLowerCase();
    let blend = rawBlend;

    if (blend === "skin") blend = "multiply";
    if (blend === "ink") blend = "multiply";

    // keep existing underpaint behavior
    if (blend === "white-ink" || blend === "overlay") blend = "white";

    // NEW: white fill mode (alpha -> white)
    if (
      blend === "whitefill" ||
      blend === "white-fill" ||
      blend === "white_fill"
    ) {
      blend = "whitefill";
    }

    const isUnderpaint = blend === "white"; // existing mode (your magic)
    const isWhiteFill = blend === "whitefill"; // NEW mode (alpha→white)

    const targetCtx = isWhiteFill ? ctxOverlay : ctxBase;

    // (optional debug — you can remove later)
    const underCtx = isUnderpaint ? ctxUnder : null;

    // ---- PREP (cached) ----
    const ec = L.edgeClip ?? edgeClip;
    const prepKey = `${size}|${blend}|${ec}|${L.url}|${L.maskUrl || ""}`;

    let prep = _prepCache.get(prepKey) || null;

    if (!prep) {
      const img = await safeLoad(L.url);
      if (!img) continue;

      if (blend === "skin") {
        prep = multiplyPass(img, { size, edgeClip: ec });
      } else if (blend === "whitefill") {
        prep = whiteMaskPass(img, { size, edgeClip: ec });
      } else {
        prep = normalPass(img, { size, edgeClip: ec });
      }

      // optional mask
      if (L.maskUrl) {
        const maskImg = await safeLoad(L.maskUrl);
        if (maskImg) {
          const ctxPrep = prep.getContext("2d");
          if (ctxPrep) {
            ctxPrep.save();
            ctxPrep.globalCompositeOperation = "destination-in";
            drawContain(ctxPrep, maskImg, size);
            ctxPrep.restore();
          }
        }
      }

      _prepCache.set(prepKey, prep);
    }

    const t = normalizeT(L.transform);
    const opacity = clamp01(L.opacity ?? 1);

    // ✅ LIVE drag override (tx/ty only)
    const txLive =
      drag?.active && drag.layerId === L.id ? drag.tx : t.tx ?? 0.5;
    const tyLive =
      drag?.active && drag.layerId === L.id ? drag.ty : t.ty ?? 0.5;

    targetCtx.save();
    targetCtx.globalAlpha = opacity;

    // base canvas blend modes
    if (targetCtx === ctxBase) {
      // multiply = classic tattoo blend
      if (blend === "multiply") {
        targetCtx.globalCompositeOperation = "multiply";
      } else {
        // normal = blocks what’s underneath (your “paper/cover” mode)
        targetCtx.globalCompositeOperation = "source-over";
      }
    } else {
      // overlay canvas always draws normal here; three.js decides how it combines
      targetCtx.globalCompositeOperation = "source-over";
    }

    // transforms
    targetCtx.translate(txLive * size, tyLive * size);
    targetCtx.rotate(t.rot || 0);

    const skewX = t.skewX || 0;
    const skewY = t.skewY || 0;
    if (skewX || skewY) {
      const shx = Math.tan(skewX);
      const shy = Math.tan(skewY);
      targetCtx.transform(1, shy, shx, 1, 0, 0);
    }

    const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
    const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;

    targetCtx.scale(sx, sy);
    targetCtx.translate(-size / 2, -size / 2);
    targetCtx.drawImage(prep, 0, 0, size, size);
    targetCtx.restore();

    // DEBUG underlay: collect only WHITE layers (same prep + same transforms)
    if (underCtx) {
      underCtx.save();
      underCtx.globalAlpha = opacity;
      underCtx.globalCompositeOperation = "source-over";

      underCtx.translate(txLive * size, tyLive * size);
      underCtx.rotate(t.rot || 0);

      const skewX2 = t.skewX || 0;
      const skewY2 = t.skewY || 0;
      if (skewX2 || skewY2) {
        const shx2 = Math.tan(skewX2);
        const shy2 = Math.tan(skewY2);
        underCtx.transform(1, shy2, shx2, 1, 0, 0);
      }

      underCtx.scale(sx, sy);
      underCtx.translate(-size / 2, -size / 2);
      underCtx.drawImage(prep, 0, 0, size, size);

      underCtx.restore();
    }
  }

  function maxAlphaOfCanvas(c) {
    const ctx = c.getContext("2d");
    const d = ctx.getImageData(0, 0, c.width, c.height).data;
    let maxA = 0;
    for (let i = 3; i < d.length; i += 4) {
      const a = d[i];
      if (a > maxA) maxA = a;
      if (maxA === 255) break;
    }
    return maxA;
  }

  console.log("[UNDERLAY] maxA =", maxAlphaOfCanvas(underlay));

  function findFirstNonZeroAlpha(c) {
    const ctx = c.getContext("2d");
    const w = c.width,
      h = c.height;
    const d = ctx.getImageData(0, 0, w, h).data;
    for (let i = 3; i < d.length; i += 4) {
      if (d[i] > 0) {
        const idx = (i - 3) / 4;
        const x = idx % w;
        const y = Math.floor(idx / w);
        return [x, y, d[i]];
      }
    }
    return null;
  }
  console.log("[UNDERLAY] firstNonZero =", findFirstNonZeroAlpha(underlay));

  return { base, overlay, underlay };
}

// pipeline.js

function _blendToMode(rawBlend) {
  let blend = (rawBlend || "skin").toLowerCase();
  if (blend === "skin") blend = "multiply";
  if (blend === "ink") blend = "multiply";
  if (blend === "white-ink" || blend === "overlay") blend = "white";
  if (blend === "whitefill" || blend === "white-fill" || blend === "white_fill")
    blend = "whitefill";
  return blend;
}

async function _prepLayerForMerge(L, { size, edgeClip }) {
  const ec = L.edgeClip ?? edgeClip;
  const blend = _blendToMode(L.blend);

  const img = await safeLoad(L.url);
  if (!img) return null;

  let prep;
  if (blend === "multiply") prep = multiplyPass(img, { size, edgeClip: ec });
  else if (blend === "whitefill")
    prep = whiteMaskPass(img, { size, edgeClip: ec });
  else prep = normalPass(img, { size, edgeClip: ec });

  // optional mask
  if (L.maskUrl) {
    const maskImg = await safeLoad(L.maskUrl);
    if (maskImg) {
      const ctxPrep = prep.getContext("2d");
      ctxPrep.save();
      ctxPrep.globalCompositeOperation = "destination-in";
      drawContain(ctxPrep, maskImg, size);
      ctxPrep.restore();
    }
  }

  return { prep, blend, ec };
}

function _drawLayerToCtx(ctx, prep, L, { size, opacityOverride = null }) {
  const t = normalizeT(L.transform);
  const opacity = clamp01(opacityOverride ?? L.opacity ?? 1);

  ctx.save();
  ctx.globalAlpha = opacity;

  // “as seen” draw rule: multiply means multiply; everything else = source-over
  const blend = _blendToMode(L.blend);
  ctx.globalCompositeOperation =
    blend === "multiply" ? "multiply" : "source-over";

  // transforms (same as buildPartComposite)
  const tx = t.tx ?? 0.5;
  const ty = t.ty ?? 0.5;

  ctx.translate(tx * size, ty * size);
  ctx.rotate(t.rot || 0);

  const skewX = t.skewX || 0;
  const skewY = t.skewY || 0;
  if (skewX || skewY) {
    const shx = Math.tan(skewX);
    const shy = Math.tan(skewY);
    ctx.transform(1, shy, shx, 1, 0, 0);
  }

  const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
  const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;

  ctx.scale(sx, sy);
  ctx.translate(-size / 2, -size / 2);
  ctx.drawImage(prep, 0, 0, size, size);

  ctx.restore();
}

/**
 * Merge two layers “as seen” into a baked PNG (dataURL).
 * bottomLayer is drawn first, then topLayer.
 */
export async function mergeLayersToDataURL(
  bottomLayer,
  topLayer,
  { size = 2048, edgeClip = 0.15 } = {}
) {
  const canUse = (L) => !!(L?.visible && L?.url);

  const bOK = canUse(bottomLayer);
  const tOK = canUse(topLayer);
  if (!bOK && !tOK) return null;

  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, size, size);

  if (bOK) {
    const pb = await _prepLayerForMerge(bottomLayer, { size, edgeClip });
    if (pb?.prep) _drawLayerToCtx(ctx, pb.prep, bottomLayer, { size });
  }

  if (tOK) {
    const pt = await _prepLayerForMerge(topLayer, { size, edgeClip });
    if (pt?.prep) _drawLayerToCtx(ctx, pt.prep, topLayer, { size });
  }

  try {
    return c.toDataURL("image/png");
  } catch (e) {
    console.warn("[merge] toDataURL failed", e);
    return null;
  }
}

/** -------- flatten ALL layers with transforms to a UV sheet -------- */
//
// Must match buildPartComposite (the 3D view) exactly, so the export looks
// like what the user designed. Previously the two used opposite rules
// (e.g. "block" multiplied in the export but covered in 3D, "ink" the
// reverse), so stacked lettering with a white "block" outline exported
// wrong.
//
// Approach:
//  1. Composite colour on an opaque WHITE base with the same blend rules
//     as the 3D view (multiply for skin/ink, source-over for block/white).
//  2. Separately build a coverage mask (union of every layer's alpha).
//  3. Keep colour only where artwork exists → transparent background
//     outside the design, which is what a stencil/print file needs.
//  White "block" areas stay opaque white (a real printable colour),
//  matching how they read on skin in the 3D view.
function _applyLayerTransform(ctx, t, size) {
  ctx.translate((t.tx ?? 0.5) * size, (t.ty ?? 0.5) * size);
  ctx.rotate(t.rot || 0);
  const skewX = t.skewX || 0;
  const skewY = t.skewY || 0;
  if (skewX || skewY) {
    ctx.transform(1, Math.tan(skewY), Math.tan(skewX), 1, 0, 0);
  }
  const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
  const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;
  ctx.scale(sx, sy);
  if (t.flipX) ctx.scale(-1, 1);
  ctx.translate(-size / 2, -size / 2);
}

// Prepare every visible layer once: loaded image + blend + prep canvas.
async function _prepareLayers(layers, { size, edgeClip }) {
  const out = [];
  // bottom → top, same as the 3D composite
  const ordered = [...(layers || [])].reverse();
  for (const L of ordered) {
    if (!L?.visible || !L?.url) continue;
    const img = await safeLoad(L.url);
    if (!img) continue;
    const blend = _blendToMode(L.blend);
    const ec = L.edgeClip ?? edgeClip;
    const prep =
      blend === "whitefill"
        ? whiteMaskPass(img, { size, edgeClip: ec })
        : normalPass(img, { size, edgeClip: ec });
    if (L.maskUrl) {
      const maskImg = await safeLoad(L.maskUrl);
      if (maskImg) {
        const pctx = prep.getContext("2d");
        pctx.save();
        pctx.globalCompositeOperation = "destination-in";
        drawContain(pctx, maskImg, size);
        pctx.restore();
      }
    }
    out.push({ L, img, blend, prep, t: normalizeT(L.transform) });
  }
  return out;
}

/**
 * Composite prepared layers into an outW×outH canvas with the SAME blend
 * rules as buildPartComposite (3D view). `applyFrame(ctx)` is applied
 * before each layer's own transform (identity for the full sheet; a
 * rotate/scale/crop frame for per-design exports).
 * Returns a canvas with a transparent background outside the artwork.
 */
function _compositePrepared(items, { size, outW, outH, applyFrame, dragPreview }) {
  const mk = () => {
    const c = document.createElement("canvas");
    c.width = outW;
    c.height = outH;
    return c;
  };
  // colour composite on white base (like the 3D base canvas)
  const colour = mk();
  const cctx = colour.getContext("2d");
  cctx.fillStyle = "#ffffff";
  cctx.fillRect(0, 0, outW, outH);
  // coverage mask (union of layer alpha)
  const mask = mk();
  const mctx = mask.getContext("2d");

  for (const it of items) {
    const t = { ...it.t };
    if (dragPreview?.active && dragPreview?.layerId === it.L.id) {
      t.tx = dragPreview.tx;
      t.ty = dragPreview.ty;
    }
    const opacity = clamp01(it.L.opacity ?? 1);
    for (const [ctx, op] of [
      [cctx, it.blend === "multiply" ? "multiply" : "source-over"],
      [mctx, "source-over"],
    ]) {
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.globalCompositeOperation = op;
      if (applyFrame) applyFrame(ctx);
      _applyLayerTransform(ctx, t, size);
      ctx.drawImage(it.prep, 0, 0, size, size);
      ctx.restore();
    }
  }

  // keep colour only where artwork exists
  cctx.save();
  cctx.globalCompositeOperation = "destination-in";
  cctx.drawImage(mask, 0, 0);
  cctx.restore();

  // Soft edges were composited against white — undo that so edges keep
  // their true colour on a transparent export: c = a·src + (1−a)·white.
  try {
    const imd = cctx.getImageData(0, 0, outW, outH);
    const d = imd.data;
    for (let i = 0; i < d.length; i += 4) {
      const A = d[i + 3];
      if (A === 0 || A === 255) continue;
      const a = A / 255;
      const w = (1 - a) * 255;
      d[i] = Math.max(0, Math.min(255, (d[i] - w) / a));
      d[i + 1] = Math.max(0, Math.min(255, (d[i + 1] - w) / a));
      d[i + 2] = Math.max(0, Math.min(255, (d[i + 2] - w) / a));
    }
    cctx.putImageData(imd, 0, 0);
  } catch (e) {
    console.warn("[composite] edge de-fringe skipped", e);
  }
  return colour;
}

export async function buildFlattenedUV(
  layers,
  {
    size = 2048,
    edgeClip = 0.15,
    guideUrl = null,
    guideAlpha = 0.35,
    dragPreview = null,
  } = {}
) {
  const items = await _prepareLayers(layers, { size, edgeClip });
  const art = _compositePrepared(items, {
    size,
    outW: size,
    outH: size,
    applyFrame: null,
    dragPreview,
  });

  const out = document.createElement("canvas");
  out.width = size;
  out.height = size;
  const octx = out.getContext("2d");
  if (guideUrl) {
    const g = await safeLoad(guideUrl);
    if (g) drawGuideCover(octx, g, size, guideAlpha ?? 0.35);
  }
  octx.drawImage(art, 0, 0);
  return out;
}

/* ---------------- per-design export ---------------- */

// Corners of a layer's visible image, in UV pixel space.
function _layerCorners(it, size) {
  const iw = it.img.naturalWidth || it.img.width;
  const ih = it.img.naturalHeight || it.img.height;
  const k = Math.min(size / iw, size / ih); // same as drawContain
  const dw = iw * k, dh = ih * k;
  const dx = (size - dw) / 2, dy = (size - dh) / 2;
  const t = it.t;
  const sx = typeof t.scaleX === "number" ? t.scaleX : t.scale || 1;
  const sy = typeof t.scaleY === "number" ? t.scaleY : t.scale || 1;
  const m = new DOMMatrix()
    .translate((t.tx ?? 0.5) * size, (t.ty ?? 0.5) * size)
    .rotate(((t.rot || 0) * 180) / Math.PI)
    .multiply(new DOMMatrix([1, Math.tan(t.skewY || 0), Math.tan(t.skewX || 0), 1, 0, 0]))
    .scale(sx, sy)
    .translate(-size / 2, -size / 2);
  return [
    [dx, dy], [dx + dw, dy], [dx + dw, dy + dh], [dx, dy + dh],
  ].map(([x, y]) => {
    const p = m.transformPoint(new DOMPoint(x, y));
    return [p.x, p.y];
  });
}

function _bbox(points) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of points) {
    if (x < x0) x0 = x; if (y < y0) y0 = y;
    if (x > x1) x1 = x; if (y > y1) y1 = y;
  }
  return { x0, y0, x1, y1 };
}

function _alphaBounds(canvas) {
  const { width: w, height: h } = canvas;
  const d = canvas.getContext("2d").getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? null : { x0, y0, x1: x1 + 1, y1: y1 + 1 };
}

/**
 * Split layers into "designs" (overlapping layers = one design, e.g.
 * stacked lettering) and render each one UPRIGHT, tightly cropped, with
 * its long edge at `target` px. Uprightness comes from undoing the
 * rotation of the design's largest layer, so a design reads the way it
 * was authored regardless of how it was rotated onto the body or how the
 * UV island is oriented.
 *
 * Returns [{ canvas, name }].
 */
export async function buildDesignExports(
  layers,
  { size = 2048, edgeClip = 0.15, target = 2048, margin = 48 } = {}
) {
  const items = await _prepareLayers(layers, { size, edgeClip });
  if (!items.length) return [];

  // group overlapping layers (union-find on padded bounding boxes)
  const boxes = items.map((it) => _bbox(_layerCorners(it, size)));
  const parent = items.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const pad = 6;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = boxes[i], b = boxes[j];
      if (a.x0 - pad < b.x1 && b.x0 - pad < a.x1 && a.y0 - pad < b.y1 && b.y0 - pad < a.y1) {
        parent[find(i)] = find(j);
      }
    }
  }
  const groups = new Map();
  items.forEach((it, i) => {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(it); // keeps bottom→top order
  });

  const results = [];
  for (const group of groups.values()) {
    // reference = largest layer in the group
    let ref = group[0], refArea = -1;
    for (const it of group) {
      const b = _bbox(_layerCorners(it, size));
      const area = (b.x1 - b.x0) * (b.y1 - b.y0);
      if (area > refArea) { refArea = area; ref = it; }
    }
    const rot = ref.t.rot || 0;
    const allPts = group.flatMap((it) => _layerCorners(it, size));
    const gb = _bbox(allPts);
    const cx = (gb.x0 + gb.x1) / 2;
    const cy = (gb.y0 + gb.y1) / 2;

    // Pass 1: render upright at UV scale to find the real (alpha) bounds.
    // Size the canvas to the rotated extent of the group.
    const rotPts = allPts.map(([x, y]) => {
      const dx = x - cx, dy = y - cy;
      const c = Math.cos(-rot), s = Math.sin(-rot);
      return [dx * c - dy * s, dx * s + dy * c];
    });
    const rb = _bbox(rotPts);
    const W1 = Math.max(1, Math.ceil(Math.max(Math.abs(rb.x0), Math.abs(rb.x1)) * 2) + 4);
    const H1 = Math.max(1, Math.ceil(Math.max(Math.abs(rb.y0), Math.abs(rb.y1)) * 2) + 4);
    const frame1 = (ctx) => {
      ctx.translate(W1 / 2, H1 / 2);
      ctx.rotate(-rot);
      ctx.translate(-cx, -cy);
    };
    const pass1 = _compositePrepared(group, { size, outW: W1, outH: H1, applyFrame: frame1 });
    const ab = _alphaBounds(pass1);
    if (!ab) continue;

    // Pass 2: re-render straight from the source layers at the scale that
    // puts the design's long edge at `target` px (crisp, not upscaled).
    const aw = ab.x1 - ab.x0, ah = ab.y1 - ab.y0;
    const acx = (ab.x0 + ab.x1) / 2 - W1 / 2;
    const acy = (ab.y0 + ab.y1) / 2 - H1 / 2;
    const k = (target - margin * 2) / Math.max(aw, ah);
    const outW = Math.round(aw * k) + margin * 2;
    const outH = Math.round(ah * k) + margin * 2;
    const frame2 = (ctx) => {
      ctx.translate(outW / 2, outH / 2);
      ctx.scale(k, k);
      ctx.translate(-acx, -acy);
      ctx.rotate(-rot);
      ctx.translate(-cx, -cy);
    };
    const canvas = _compositePrepared(group, { size, outW, outH, applyFrame: frame2 });

    const top = group[group.length - 1].L;
    results.push({ canvas, name: top.name || "design" });
  }
  return results;
}

/* Optional blend map if you ever need to switch on it elsewhere */
export const BLEND_MAP = {
  normal: "source-over",
  multiply: "multiply",
  screen: "screen",
  overlay: "overlay",
  darken: "darken",
  lighten: "lighten",
  difference: "difference",
  exclusion: "exclusion",
  "soft-light": "overlay", // Canvas2D fallback
  "hard-light": "overlay",
  "color-burn": "multiply",
  "color-dodge": "screen",
};
